import { getRatePageSettings, ratesKeys } from '@features/rates';
import { getRoomPageSettings, getStaticRooms, roomsKeys } from '@features/rooms';
import {
  getAllSpaPackagesPrefetch,
  getAvailableSpaTreatmentsPrefetch,
  getSpaClassesPagePrefetch,
  getSpaDaysPagePrefetch,
  getSpaFacilitiesPagePrefetch,
  getSpaStaysPagePrefetch,
  getSpaTreatmentsPagePrefetch,
} from '@features/spa/hooks';
import { seedTablesPageQuery } from '@features/tables/hooks/useGetTablesPage';
import { PagePropsWithSSR } from '@features/state';
import { HomeVersion } from '@features/Home';
import { getFeaturedModuleVisibility } from '@features/Home/useFeaturedModuleVisibility';
import * as entityActions from '@store/entity/actions';
import { entitySelectors } from '@store/entity/selectors';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { NextPage, NextPageContext } from 'next';
import NextSeo from 'next-seo';
import Head from 'next/head';
import { Property } from 'shared-types/Property';

type HomePageProps = PagePropsWithSSR<{
  property: Property;
  language: string;
  url: string;
}>;

const HomePage: NextPage<HomePageProps> = ({ property, language, url }) => {
  const pageTitle = property.propertyTitle
    ? `${property.name} | ${property.propertyTitle}`
    : property.name;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <HomeVersion id={property.id} property={property} language={language} />
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: property?.socialFallbackImage?.url }],
          },
        }}
      />
    </>
  );
};

HomePage.getInitialProps = async ({
  store,
  property,
  language,
  url,
  moduleStatuses,
}: NextPageContext) => {
  const { id: siteId } = property;

  await store.dispatch(entityActions.fetchHomepage(property.id, language));

  const client = new QueryClient();
  const dataRequests: Array<Promise<void>> = [];

  if (moduleStatuses.roomsEnabled) {
    const roomsPageQuery = client.prefetchQuery({
      queryKey: roomsKeys.roomSettings({ siteId, language }),
      queryFn: () => getRoomPageSettings({ siteId, language }),
      cacheTime: Infinity,
      staleTime: Infinity,
    });

    const staticRoomsQuery = client.prefetchQuery({
      queryKey: roomsKeys.staticRooms({ siteId, language }),
      queryFn: () => getStaticRooms({ siteId, language }),
      cacheTime: Infinity,
      staleTime: Infinity,
    });

    const ratesPageQuery = client.prefetchQuery({
      queryKey: ratesKeys.rateSettings({ siteId, language }),
      queryFn: () => getRatePageSettings({ siteId, language }),
      cacheTime: Infinity,
      staleTime: Infinity,
    });

    dataRequests.push(roomsPageQuery, staticRoomsQuery, ratesPageQuery);
  }

  const { isFeaturedSpaDaysVisible, isFeaturedSpaTreatmentsVisible } = getFeaturedModuleVisibility(
    property.modulesExcludingDisabled
  );

  if (isFeaturedSpaDaysVisible) {
    dataRequests.push(
      getAllSpaPackagesPrefetch(client, property.id, language),
      getSpaDaysPagePrefetch(client, property.id, language)
    );
  }

  if (property.modulesExcludingDisabled?.spa?.features?.stays?.enabled) {
    dataRequests.push(getSpaStaysPagePrefetch(client, property.id, language));
  }

  if (isFeaturedSpaTreatmentsVisible) {
    dataRequests.push(
      getAvailableSpaTreatmentsPrefetch(client, property.id, language, null, null, 6, true),
      getSpaTreatmentsPagePrefetch(client, property.id, language)
    );
  }

  if (property.modulesExcludingDisabled?.spa?.features?.classes?.enabled) {
    dataRequests.push(getSpaClassesPagePrefetch(client, property.id, language));
  }

  if (property.modulesExcludingDisabled?.spa?.features?.facilities?.enabled) {
    dataRequests.push(getSpaFacilitiesPagePrefetch(client, property.id, language));
  }

  if (property.isTablesIntegrated) {
    dataRequests.push(
      store
        .dispatch(entityActions.fetchTablesPage(property.id, language))
        .then(() =>
          seedTablesPageQuery(
            client,
            property.id,
            language,
            entitySelectors.getTablesPage(store.getState())
          )
        )
    );
  }

  if (property.modulesExcludingDisabled?.golf?.enabled) {
    dataRequests.push(store.dispatch(entityActions.fetchGolfPage(property.id, language)));
  }

  if (property.isRetailEnabled) {
    dataRequests.push(store.dispatch(entityActions.fetchProducts(property.id, language)));
  }

  await Promise.all(dataRequests);

  return {
    url,
    language,
    property,
    socialFallbackImage: property.socialFallbackImage,
    dehydratedState: dehydrate(client),
  };
};

export default HomePage;
